import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import './CasinoThumbnail.css';
import { useSelector } from 'react-redux';
import { useFitTextToRows } from '../../../../../../shared/src/hooks/use-fit-text-to-rows.hook';
import { Image } from '../../../../../../shared/src/components/Image';
import JackpotWrapper from '../JackpotWrapper/JackpotWrapper';
import { CasinoThumbnailOverlayBasic } from '../CasinoThumbnailOverlayBasic';
import { CasinoThumbnailOverlayStripsUI } from '../CasinoThumbnailOverlayStripsUI';
import { CasinoThumbnailInit } from '../CasinoThumbnailInit';
import { useThumbnailSize } from '../../../../hooks/use-thumbnail-size.hook';
import { JackpotSourcesEnum } from '../../../../../../shared/src/enums/jackpot-sources.enum';

export const CasinoThumbnail = ({
    name,
    images,
    badges,
    overlayBadges,
    openGameHandler,
    isMobile = true,
    thumbnailSize: argumentThumbnailSize,
    id,
    imageType,
    displayNameOnStripsGridUI = false,
}) => {
    const [overlayActive, setOverlayActive] = useState(false);
    const [jackpotExpires, setJackpotExpires] = useState(null);
    const [jackpotSource, setJackpotSource] = useState(JackpotSourcesEnum.PARI_PLAY);
    const [jackpotId, setJackpotId] = useState(null);
    const {
        jackpots,
        randomJackpotToGame,
        ngProgressiveJackpots: {
            idsListByGameId: ngPjpIdsByGameId,
            amountsById: ngPjpAmountsById,
        },
    } = useSelector((state) => state.jackpots);
    const {
        config: { showStripsGridUI, intJackpotsPremium, jackpotTickerNg },
        platformReady,
        areFavoritesEnabled,
    } = useSelector((state) => state.app);
    const { isLoggedIn, balance } = useSelector((state) => state.player);
    const ctaTextRef = useRef();
    const thumbnailRef = useRef();
    const { thumbnailSize: calculatedThumbnailSize } = useThumbnailSize(thumbnailRef);
    const showFavorites = areFavoritesEnabled && platformReady && isLoggedIn;
    const thumbnailSize = argumentThumbnailSize || calculatedThumbnailSize;

    // adjust "Play game_name" text to 2 rows
    useFitTextToRows(
        ctaTextRef,
        2,
        [ctaTextRef, overlayActive, thumbnailSize],
    );

    useEffect(() => {
        if (jackpotTickerNg && ngPjpIdsByGameId?.[id]?.length > 0) {
            setJackpotSource(JackpotSourcesEnum.NG_PJP);
        } else {
            setJackpotSource(JackpotSourcesEnum.PARI_PLAY);
        }
    }, [id, ngPjpIdsByGameId]);

    // Set random JP id if jackpot's source is PariPlay
    useEffect(() => {
        if (jackpotSource === JackpotSourcesEnum.PARI_PLAY) {
            const randomJackpotId = randomJackpotToGame && Object.keys(randomJackpotToGame).length > 0 &&
                randomJackpotToGame[id];

            if (randomJackpotId) {
                setJackpotExpires(jackpots[randomJackpotId]?.expiresIn);
                setJackpotId(randomJackpotId);
            }
        }
    }, [id, jackpots, jackpotSource]);

    // Set JP id w/ the highest amount if jackpot's source is NeoGames Progressive JP
    useEffect(() => {
        if (jackpotSource === JackpotSourcesEnum.NG_PJP) {
            const currencyCode = balance?.Currency;
            const pjpIds = ngPjpIdsByGameId[id];
            let highestAmount = 0;
            let highestPjpId = null;

            pjpIds.forEach(pjpId => {
                const jackpotAmount = ngPjpAmountsById
                    ?.[currencyCode]
                    ?.[pjpId];
                if (jackpotAmount && jackpotAmount > highestAmount) {
                    highestAmount = jackpotAmount;
                    highestPjpId = pjpId;
                }
            });

            if (highestPjpId) {
                setJackpotId(highestPjpId);
            }
        }
    }, [id, ngPjpIdsByGameId, ngPjpAmountsById, jackpotSource]);

    const getThumbnailEventHandlers = () => {
        let eventHandlers = {
            onClick: openGameHandler,
        };

        // show and hide overlay on hover for desktop
        if (!isMobile) {
            eventHandlers = {
                onMouseEnter: () => { setOverlayActive(true); },
                onMouseLeave: () => { setOverlayActive(false); },
            };
        }

        return eventHandlers;
    };

    const className = classNames(
        'casino-thumbnail',
        `casino-thumbnail--${thumbnailSize}`,
        {
            'casino-thumbnail--strips-grid-ui': showStripsGridUI,
        },
    );

    const jackpotWrapperProps = {
        jackpotSource,
        jackpotId,
    };

    const showInit = (badges?.length > 0 || (jackpotExpires && intJackpotsPremium) || showFavorites);

    const CasinoThumbnailOverlay = showStripsGridUI
        ? CasinoThumbnailOverlayStripsUI
        : CasinoThumbnailOverlayBasic;

    let extraCasinoThumbnailOverlayParams = {
        name,
        ctaTextRef,
    };

    if (showStripsGridUI && !displayNameOnStripsGridUI) {
        extraCasinoThumbnailOverlayParams = {};
    }

    return (
        <div
            className={className}
            ref={thumbnailRef}
            {...getThumbnailEventHandlers()}
        >
            {JackpotWrapper(Image, thumbnailSize, jackpotWrapperProps)({
                className: 'casino-thumbnail__img',
                images,
                alt: name,
                imageType,
            })}

            {showInit
                && <CasinoThumbnailInit
                    gameId={id}
                    jackpotExpires={jackpotExpires}
                    badges={badges}
                    showFavorites={showFavorites}
                    showStripsGridUI={showStripsGridUI}
                />
            }

            {/*state on hover (or mobile first click)*/}
            {overlayActive && <CasinoThumbnailOverlay
                overlayBadges={overlayBadges}
                openGameHandler={openGameHandler}
                showFavorites={showFavorites}
                gameId={id}
                {...extraCasinoThumbnailOverlayParams}
            />}
        </div>
    );
};
